import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, GearIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ManageAccessDialog from "@/components/ManageAccessDialog";

const empty = { email: "", password: "", name: "", role: "mechanic" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [manageId, setManageId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const roleTone = (r) => (r === "admin" ? "info" : r === "manager" ? "warning" : r === "dispatcher" ? "info" : "neutral");

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Admin" title="Users" subtitle="Create accounts and grant fine-grained access to your team.">
        <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-user-button">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add User
        </Button>
      </PageHeader>

      <div className="p-8">
        <div className="bg-white border border-slate-300 rounded-sm">
          <table className="fleet-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-semibold">{u.name}</td>
                  <td className="font-mono">{u.email}</td>
                  <td><Badge tone={roleTone(u.role)}>{u.role}</Badge></td>
                  <td>
                    {u.is_active === false ? (
                      <Badge tone="danger" testid={`status-${u.email}`}>Inactive</Badge>
                    ) : (
                      <Badge tone="success" testid={`status-${u.email}`}>Active</Badge>
                    )}
                  </td>
                  <td className="font-mono text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => setManageId(u.id)}
                        className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-xs font-semibold"
                        data-testid={`manage-access-${u.email}`}
                      >
                        <GearIcon size={14} weight="bold" /> Manage Access
                      </button>
                      {u.id !== user?.id && (
                        <button
                          onClick={() => del(u)}
                          className="text-red-600 hover:underline text-xs font-semibold"
                          data-testid={`del-user-${u.email}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Add user</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid="user-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Password *</Label>
              <Input type="password" required minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-sm" data-testid="user-password" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher</SelectItem>
                  <SelectItem value="mechanic">Mechanic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="user-save">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ManageAccessDialog
        open={!!manageId}
        onOpenChange={(v) => { if (!v) setManageId(null); }}
        userId={manageId}
        onSaved={() => { load(); }}
      />
    </div>
  );
}
